import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Skeleton — gray pulsing placeholder for content that's loading.
 *
 * Tailwind only, no JS animation. Matches consumer's `--muted` token.
 *
 * Common patterns are exposed as variants instead of asking each consumer to
 * remember the right radius/aspect:
 *   - `text` (single line)
 *   - `paragraph` (multiple lines, decreasing widths)
 *   - `avatar` (circle)
 *   - `card` (rounded rect)
 *   - `button` (pill, sized like Button default)
 *
 * For custom shapes, use `variant="block"` and pass your own className.
 */
const skeletonVariants = cva("", {
  variants: {
    variant: {
      block: "rounded-md",
      text: "h-4 rounded-md",
      avatar: "rounded-full",
      card: "rounded-2xl",
      button: "h-10 rounded-full",
    },
    /**
     * Loading animation:
     * - `pulse` (default) — opacity pulse on a flat muted surface. Cheap,
     *   blends into existing shadcn skeletons.
     * - `shimmer` — gradient sweep. Reads as "data is actively loading" and
     *   pairs well with card-grid skeletons where the eye wants direction.
     * - `none` — static. For SSR pre-hydration or contexts where the
     *   consumer wants explicit control. Reduced-motion auto-degrades both
     *   `pulse` and `shimmer` via `motion-reduce:animate-none`.
     */
    animation: {
      pulse: "animate-pulse bg-muted motion-reduce:animate-none",
      shimmer:
        "bg-muted bg-[linear-gradient(110deg,transparent,40%,hsl(var(--muted-foreground)/0.12),60%,transparent)] bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none motion-reduce:bg-muted",
      none: "bg-muted",
    },
  },
  defaultVariants: {
    variant: "block",
    animation: "pulse",
  },
});

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, animation, ...props }, ref) => (
    <div ref={ref} className={cn(skeletonVariants({ variant, animation }), className)} {...props} />
  ),
);
Skeleton.displayName = "Skeleton";

/**
 * SkeletonParagraph — convenience wrapper that renders 3 text-skeletons with
 * decreasing widths (90%, 100%, 65%) for the typical paragraph placeholder.
 */
export interface SkeletonParagraphProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of lines. Default 3. */
  lines?: number;
}

export const SkeletonParagraph = React.forwardRef<HTMLDivElement, SkeletonParagraphProps>(
  ({ className, lines = 3, ...props }, ref) => {
    const widths = ["w-[90%]", "w-full", "w-[65%]", "w-[80%]", "w-[55%]"];
    return (
      <div ref={ref} className={cn("space-y-2", className)} {...props}>
        {Array.from({ length: lines }).map((_, i) => (
          <Skeleton key={i} variant="text" className={widths[i % widths.length]} />
        ))}
      </div>
    );
  },
);
SkeletonParagraph.displayName = "SkeletonParagraph";
